import { Fragment, useState, useEffect, useRef } from 'react'
import { Disclosure, Menu, Transition } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { useSelector, useDispatch } from 'react-redux';
import { setLogout } from '../../state';
import { FaUserCircle, FaHome, FaCalendarAlt, FaStore, FaUtensils, FaBell, FaTools } from 'react-icons/fa';
import { BsChatSquareTextFill } from 'react-icons/bs';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaUsers } from 'react-icons/fa';
import NotificationPanel from '../../components/NotificationPanel';
import { selectUnreadCount, addConnectionRequestNotification } from '../../state/notificationSlice';
import store from '../../state';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const NavIcon = ({ icon: Icon, to, label, color }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <motion.div
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      className="relative"
    >
      <Link
        to={to}
        className={`flex flex-col items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
          isActive ? 'text-pri-yellow bg-pri-dark/30' : 'text-pri-white hover:text-pri-yellow'
        }`}
      >
        <Icon className={`h-5 w-5 ${color} ${isActive ? 'animate-bounce' : ''}`} />
        <span className="mt-1 text-xs opacity-90">{label}</span>
      </Link>
    </motion.div>
  );
};

const NavIconImage = ({ imgSrc, to, label, color }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <motion.div
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      className="relative"
    >
      <Link
        to={to}
        className={`flex flex-col items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
          isActive ? 'text-pri-yellow bg-pri-dark/30' : 'text-pri-white hover:text-pri-yellow'
        }`}
      >
        <motion.div 
          className={`relative h-9 w-9 ${isActive ? 'animate-pulse' : ''}`}
          animate={isActive ? { 
            y: [0, -4, 0],
            transition: { repeat: Infinity, duration: 2, ease: "easeInOut" }
          } : {}}
        >
          {/* Add subtle glow effect behind the icon */}
          <div className={`absolute inset-0 rounded-full blur-lg ${isActive ? 'bg-pri-yellow/30' : 'bg-soft-pink/20 opacity-0 group-hover:opacity-100'} transition-all duration-300`}></div>
          
          <img src={imgSrc} alt={label} className="w-full h-full object-contain relative z-10" />
        </motion.div>
        <span className="mt-1 text-xs opacity-90">{label}</span>
      </Link>
    </motion.div>
  );
};

// Mobile bottom navigation icon
const MobileNavIcon = ({ imgSrc, to, label }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`flex flex-col items-center justify-center ${
        isActive ? 'text-pri-yellow' : 'text-pri-white'
      }`}
    >
      <div className={`relative w-6 h-6 ${isActive ? 'scale-110 transition-transform duration-300' : ''}`}>
        <img src={imgSrc} alt={label} className="w-full h-full object-contain" />
      </div>
      <span className="mt-0.5 text-[10px] font-medium">{label}</span>
    </Link>
  );
};

// Define a flag to disable notification features
const NOTIFICATIONS_ENABLED = false; // Set to false to disable notifications

const Topbar = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const notificationButtonRef = useRef(null);
  const unreadCount = useSelector(selectUnreadCount);
  const [localUnreadCount, setLocalUnreadCount] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(false);

  // Redirect to login if user is null (token expired)
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const handleSocketEvent = (event) => {
      // Handle socket event silently
    };

    document.addEventListener('socketEvent', handleSocketEvent);
    return () => document.removeEventListener('socketEvent', handleSocketEvent);
  }, [user, navigate]);

  useEffect(() => {
    const handleConnectionStatus = (event) => {
      // Handle connection status silently
    };

    document.addEventListener('connectionStatus', handleConnectionStatus);
    return () => document.removeEventListener('connectionStatus', handleConnectionStatus);
  }, []);

  useEffect(() => {
    const currentCount = unreadCount || 0;
    setLocalUnreadCount(currentCount);
  }, [unreadCount]);

  const handleManualRefresh = () => {
    // Handle manual refresh silently
  };

  const userNavigation = [
    { name: 'Your Profile', href: '/profile' },
    { name: 'Sign out', href: '#', onClick: (e) => {
      e.preventDefault();
      handleLogout();
    }},
  ]

  const handleLogout = () => {
    // Dispatch the logout action
    dispatch(setLogout());
    // Navigate user to login page
    navigate('/login');
  };
  
  // Handle notification action
  const handleNotificationAction = (type, data) => {
    // Handle notification action silently
  };

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isNotificationOpen &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(event.target) &&
        !event.target.closest('.notification-panel')
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen]);

  // If user is null, render a minimal version of the component
  if (!user) {
    return null; // Return null to prevent rendering anything while redirecting
  }

  return (
    <>
      <Disclosure as="nav" className="bg-sec-dark/95 backdrop-blur-md shadow-lg sticky top-0 z-50">
        {({ open }) => (
          <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex items-center">
                  <motion.div
                    className="flex-shrink-0 flex items-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link to="/" className="flex items-center space-x-2">
                      <motion.img
                        initial={{ rotate: -180, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="block h-8 w-auto"
                        src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
                        alt="Jingleez"
                      />
                      <motion.h1 
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-2xl font-bold text-soft-pink"
                      >
                        Jingleez
                      </motion.h1>
                    </Link>
                  </motion.div>
                  <div className="hidden sm:ml-6 sm:flex sm:space-x-4">
                    <NavIconImage imgSrc={`${process.env.REACT_APP_CDN_URL}/icons/home.png`} to="/home" label="Home" color="text-pri-yellow" />
                    <NavIconImage imgSrc={`${process.env.REACT_APP_CDN_URL}/icons/calendar.png`} to="/calendar" label="Calendar" color="text-pri-green" />
                    <NavIconImage imgSrc={`${process.env.REACT_APP_CDN_URL}/icons/meal-planner.png`} to="/meal-planner" label="Meal Planner" color="text-pri-yellow" />
                    {/* Disabled navigation items
                    <NavIcon icon={FaStore} to="/store" label="Store" color="text-blue-500" />
                    <NavIcon icon={FaUtensils} to="/food" label="Food" color="text-red-500" />
                    <NavIcon icon={FaTools} to="/service" label="Services" color="text-purple-500" />
                    */}
                    <NavIconImage imgSrc={`${process.env.REACT_APP_CDN_URL}/icons/connections.png`} to="/connect" label="Connect" color="text-pink-500" />
                  </div>
                </div>
                <div className="hidden sm:ml-6 sm:flex sm:items-center space-x-4">
                  {/* Beginning of Notification Button - Conditionally rendered based on NOTIFICATIONS_ENABLED flag */}
                  {NOTIFICATIONS_ENABLED && (
                    <motion.div className="relative">
                      <motion.button
                        ref={notificationButtonRef}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="relative p-2 rounded-full text-pri-white hover:text-pri-yellow transition-colors"
                        onClick={() => {
                          handleManualRefresh();
                          setIsNotificationOpen(!isNotificationOpen);
                        }}
                      >
                        <FaBell className="h-5 w-5" />
                        {localUnreadCount > 0 && (
                          <motion.span 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-1 -right-1 flex items-center justify-center"
                          >
                            {localUnreadCount > 9 ? (
                              <span className="bg-pri-yellow text-pri-dark text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                                9+
                              </span>
                            ) : (
                              <span className="bg-pri-yellow rounded-full h-4 w-4 flex items-center justify-center">
                                {localUnreadCount > 0 && (
                                  <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pri-yellow opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-pri-yellow"></span>
                                  </span>
                                )}
                              </span>
                            )}
                          </motion.span>
                        )}
                      </motion.button>
                      
                      <div className="notification-panel">
                        <NotificationPanel 
                          isOpen={isNotificationOpen} 
                          onClose={() => setIsNotificationOpen(false)}
                          onAction={handleNotificationAction}
                        />
                      </div>
                    </motion.div>
                  )}
                  {/* End of Notification Button */}

                  <Menu as="div" className="relative">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Menu.Button className="flex items-center space-x-3 rounded-full bg-gradient-to-br from-soft-pink/30 to-mint-green/20 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-soft-pink border border-soft-pink/30 shadow-md hover:from-soft-pink/40 hover:to-mint-green/30 transition-all duration-300">
                        {user && user.profile_photo_url ? (
                          <img className="h-8 w-8 rounded-full" src={user.profile_photo_url} alt="" />
                        ) : (
                          <FaUserCircle className="h-8 w-8 text-pri-white" />
                        )}
                        <span className="hidden md:block text-pri-white">{user?.name || 'User'}</span>
                      </Menu.Button>
                    </motion.div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-xl shadow-xl py-1 bg-gradient-to-br from-pri-dark to-sec-dark border border-soft-pink/20 backdrop-blur-md focus:outline-none overflow-hidden z-50">
                        <div className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              {user && user.profile_photo_url ? (
                                <img className="h-10 w-10 rounded-full" src={user.profile_photo_url} alt="" />
                              ) : (
                                <FaUserCircle className="h-10 w-10 text-pri-white" />
                              )}
                            </div>
                            <div className="ml-3 min-w-0 flex-1 overflow-hidden">
                              <div className="text-base font-medium text-pri-white truncate">{user?.name || 'User'}</div>
                              <div className="text-sm font-medium text-pri-gray truncate">{user?.email || 'No email'}</div>
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-pri-gray/20">
                          {userNavigation.map((item) => (
                            <Menu.Item key={item.name}>
                              {({ active }) => (
                                <motion.a
                                  whileHover={{ x: 6 }}
                                  href={item.href}
                                  onClick={item.onClick}
                                  className={classNames(
                                    active ? 'bg-gradient-to-r from-soft-pink/20 to-mint-green/10 text-pri-yellow' : 'text-pri-white',
                                    'block px-4 py-3 text-sm transition-all duration-200 hover:shadow-inner'
                                  )}
                                >
                                  {item.name}
                                </motion.a>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>

                <div className="flex items-center sm:hidden">
                  <Menu as="div" className="relative ml-3">
                    <div>
                      <Menu.Button className="inline-flex items-center justify-center p-2 rounded-lg text-pri-gray hover:text-pri-yellow hover:bg-pri-dark focus:outline-none">
                        <span className="sr-only">Open user menu</span>
                        <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="origin-top-right absolute right-0 mt-2 w-64 rounded-xl shadow-xl py-1 bg-gradient-to-br from-pri-dark to-sec-dark border border-soft-pink/20 backdrop-blur-md focus:outline-none overflow-hidden z-50">
                        <div className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              {user && user.profile_photo_url ? (
                                <img className="h-10 w-10 rounded-full" src={user.profile_photo_url} alt="" />
                              ) : (
                                <FaUserCircle className="h-10 w-10 text-pri-white" />
                              )}
                            </div>
                            <div className="ml-3 min-w-0 flex-1 overflow-hidden">
                              <div className="text-base font-medium text-pri-white truncate">{user?.name || 'User'}</div>
                              <div className="text-sm font-medium text-pri-gray truncate">{user?.email || 'No email'}</div>
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-pri-gray/20">
                          {userNavigation.map((item) => (
                            <Menu.Item key={item.name}>
                              {({ active }) => (
                                <motion.a
                                  whileHover={{ x: 6 }}
                                  href={item.href}
                                  onClick={item.onClick}
                                  className={classNames(
                                    active ? 'bg-gradient-to-r from-soft-pink/20 to-mint-green/10 text-pri-yellow' : 'text-pri-white',
                                    'block px-4 py-3 text-sm transition-all duration-200 hover:shadow-inner'
                                  )}
                                >
                                  {item.name}
                                </motion.a>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>
          </>
        )}
      </Disclosure>

      {/* Mobile notification panel - conditionally render based on NOTIFICATIONS_ENABLED flag */}
      {NOTIFICATIONS_ENABLED && (
        <div className="sm:hidden notification-panel fixed inset-0 z-50 pointer-events-none">
          <div className="pointer-events-auto">
            <NotificationPanel 
              isOpen={isNotificationOpen} 
              onClose={() => setIsNotificationOpen(false)}
              onAction={handleNotificationAction}
            />
          </div>
        </div>
      )}

      {/* Mobile Navigation Below Header - only visible on small screens */}
      <div className="mobile-nav-top sm:hidden sticky top-16 left-0 right-0 bg-pri-dark/90 backdrop-blur-md border-b border-white/10 shadow-md z-40">
        <div className="flex items-center justify-around px-8 py-2">
          <MobileNavIcon imgSrc={`${process.env.REACT_APP_CDN_URL}/icons/home.png`} to="/home" label="Home" />
          <MobileNavIcon imgSrc={`${process.env.REACT_APP_CDN_URL}/icons/calendar.png`} to="/calendar" label="Calendar" />
          <MobileNavIcon imgSrc={`${process.env.REACT_APP_CDN_URL}/icons/meal-planner.png`} to="/meal-planner" label="Meals" />
          <MobileNavIcon imgSrc={`${process.env.REACT_APP_CDN_URL}/icons/connections.png`} to="/connect" label="Connect" />
        </div>
      </div>
      
      {/* Add a small gap after the navigation */}
      <div className="sm:hidden h-1 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
    </>
  );
}

export default Topbar;
